import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, PhoneIcon, MailIcon, DownloadIcon, FileSpreadsheetIcon, ShieldIcon, ArchiveIcon, ArchiveRestoreIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon, LockIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Insurance as InsuranceType } from '../../types';
import { exportInsurancesToExcel } from '../../utils/excelExport';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
export function Insurance() {
  const {
    insurances,
    addInsurance,
    updateInsurance,
    deleteInsurance
  } = useData();
  const {
    user
  } = useAuth();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('insurance');
  const isAdmin = user?.role === 'administrador';
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedInsurance, setSelectedInsurance] = useState<InsuranceType | null>(null);
  const [isActionConfirmOpen, setIsActionConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState<'archive' | 'deactivate' | 'delete' | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    coverageType: '',
    phone: '',
    email: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);
  // Archive/Deactivate Logic
  const STORAGE_KEY = 'savidna-insurance-status';
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setItemStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(itemStatuses));
  }, [itemStatuses]);
  const getItemStatus = (id: string): 'active' | 'archived' | 'deactivated' => itemStatuses[id] || 'active';
  const archiveItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    updateInsurance(id, {
      status: 'archived'
    } as any).catch(e => console.error('Error archiving insurance in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const deactivateItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    updateInsurance(id, {
      status: 'deactivated'
    } as any).catch(e => console.error('Error deactivating insurance in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const restoreItem = (id: string) => {
    setItemStatuses(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    updateInsurance(id, {
      status: 'active'
    } as any).catch(e => console.error('Error restoring insurance in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const filteredInsurances = insurances.filter(i => {
    const matchesSearch = i.name.toLowerCase().includes(searchTerm.toLowerCase()) || i.company.toLowerCase().includes(searchTerm.toLowerCase());
    const status = getItemStatus(i.id);
    return matchesSearch && status === viewMode;
  });
  const {
    paginatedItems: paginatedInsurances,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(filteredInsurances);
  // Counts for tabs
  const activeCount = insurances.filter(i => getItemStatus(i.id) === 'active').length;
  const archivedCount = insurances.filter(i => getItemStatus(i.id) === 'archived').length;
  const deactivatedCount = insurances.filter(i => getItemStatus(i.id) === 'deactivated').length;
  const openNewForm = () => {
    if (!canCreate('insurance')) return;
    setSelectedInsurance(null);
    setFormData({
      name: '',
      company: '',
      coverageType: '',
      phone: '',
      email: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (insurance: InsuranceType) => {
    if (!canEdit('insurance')) return;
    setSelectedInsurance(insurance);
    setFormData({
      name: insurance.name,
      company: insurance.company,
      coverageType: insurance.coverageType,
      phone: insurance.phone,
      email: insurance.email
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openActionConfirm = (insurance: InsuranceType, type: 'archive' | 'deactivate' | 'delete') => {
    setSelectedInsurance(insurance);
    setActionType(type);
    setIsActionConfirmOpen(true);
  };
  const handleAction = () => {
    if (!selectedInsurance || !actionType) return;
    if (actionType === 'archive') {
      archiveItem(selectedInsurance.id);
    } else if (actionType === 'deactivate') {
      deactivateItem(selectedInsurance.id);
    } else if (actionType === 'delete') {
      deleteInsurance(selectedInsurance.id);
      setIsActionConfirmOpen(false);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (!formData.company) errors.company = 'La compañía es requerida';
    if (!formData.coverageType) errors.coverageType = 'El tipo de cobertura es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    if (selectedInsurance) {
      updateInsurance(selectedInsurance.id, formData);
    } else {
      addInsurance(formData);
    }
    setIsFormOpen(false);
  };
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportInsurancesToExcel(insurances);
    setIsExporting(false);
  };
  return <div className="space-y-6" data-id="element-1644">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 p-6 text-white shadow-xl" data-id="element-1645">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-1646" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-1647">
          <div data-id="element-1648">
            <div className="flex items-center gap-3 mb-2" data-id="element-1649">
              <div className="p-2.5 bg-white/15 backdrop-blur-sm rounded-xl" data-id="element-1650">
                <ShieldIcon className="w-6 h-6" data-id="element-1651" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-1652">Seguros</h1>
            </div>
            <p className="text-teal-100 max-w-xl" data-id="element-1653">
              Gestión de seguros médicos y coberturas
              {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-1654">
                  Solo lectura
                </span>}
            </p>
          </div>
          <div className="flex gap-3" data-id="element-1655">
            {isAdmin && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-1657" />} onClick={handleExportToExcel} isLoading={isExporting} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-1656">
                {isExporting ? 'Exportando...' : 'Exportar Excel'}
              </Button>}
            {canCreate('insurance') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1659" />} onClick={openNewForm} className="bg-white text-teal-700 hover:bg-teal-50 shadow-lg shadow-teal-900/20" data-id="element-1658">
                Nuevo Seguro
              </Button>}
          </div>
        </div>
      </div>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-1660">
          <LockIcon className="w-4 h-4" data-id="element-1661" />
          <span data-id="element-1662">
            Solo tienes permisos de lectura. No puedes crear, editar o eliminar
            seguros.
          </span>
        </div>}

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-end" data-id="element-1663">
        <div className="w-full md:w-96 relative" data-id="element-1664">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1665" />
          <Input placeholder="Buscar seguros..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-1666" />
        </div>

        {/* View Mode Tabs */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-1667">
          {[{
          key: 'active',
          label: 'Activos',
          count: activeCount,
          icon: CheckCircleIcon
        }, {
          key: 'archived',
          label: 'Archivados',
          count: archivedCount,
          icon: ArchiveIcon
        }, {
          key: 'deactivated',
          label: 'Desactivados',
          count: deactivatedCount,
          icon: XCircleIcon
        }].map(tab => <button key={tab.key} onClick={() => setViewMode(tab.key as any)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-1668">
              <tab.icon className="w-4 h-4" data-id="element-1669" />
              {tab.label}
              <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-1670">
                {tab.count}
              </span>
            </button>)}
        </div>
      </div>

      {/* Insurance List */}
      <Card padding="none" className="overflow-hidden border-0 shadow-lg" data-id="element-1671">
        <div className="h-1 bg-gradient-to-r from-teal-500 to-cyan-500" data-id="element-1672" />
        <div className="overflow-x-auto" data-id="element-1673">
          <table className="w-full" data-id="element-1674">
            <thead data-id="element-1675">
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-1676">
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1677">
                  Nombre
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1678">
                  Compañía
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1679">
                  Cobertura
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1680">
                  Contacto
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1681">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-1682">
              {filteredInsurances.length === 0 ? <tr data-id="element-1683">
                  <td colSpan={5} className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-1684">
                    <div className="flex flex-col items-center justify-center" data-id="element-1685">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-1686">
                        <ShieldIcon className="w-8 h-8 text-gray-400" data-id="element-1687" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-1688">
                        No se encontraron seguros
                      </h3>
                      <p className="text-sm" data-id="element-1689">
                        {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'Comienza registrando un nuevo seguro' : 'No hay seguros en esta vista'}
                      </p>
                    </div>
                  </td>
                </tr> : paginatedInsurances.map(insurance => <tr key={insurance.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group" data-id="element-1690">
                    <td className="py-4 px-6" data-id="element-1691">
                      <div className="flex items-center gap-3" data-id="element-1692">
                        <div className="w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400" data-id="element-1693">
                          <ShieldIcon className="w-4 h-4" data-id="element-1694" />
                        </div>
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-1695">
                          {insurance.name}
                        </p>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-900 dark:text-gray-100" data-id="element-1696">
                      {insurance.company}
                    </td>
                    <td className="py-4 px-6" data-id="element-1697">
                      <Badge variant="info" className="bg-sky-50 text-sky-700 border border-sky-100" data-id="element-1698">
                        {insurance.coverageType}
                      </Badge>
                    </td>
                    <td className="py-4 px-6" data-id="element-1699">
                      <div className="space-y-1" data-id="element-1700">
                        {insurance.phone && <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1.5" data-id="element-1701">
                            <PhoneIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-1702" />{' '}
                            {insurance.phone}
                          </p>}
                        {insurance.email && <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1.5" data-id="element-1703">
                            <MailIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-1704" />{' '}
                            {insurance.email}
                          </p>}
                      </div>
                    </td>
                    <td className="py-4 px-6" data-id="element-1705">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1706">
                        {viewMode === 'active' ? <>
                            {canEdit('insurance') && <button onClick={() => openEditForm(insurance)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Editar" data-id="element-1707">
                                <EditIcon className="w-4 h-4" data-id="element-1708" />
                              </button>}
                            {canDelete('insurance') && <>
                                <button onClick={() => openActionConfirm(insurance, 'archive')} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-1709">
                                  <ArchiveIcon className="w-4 h-4" data-id="element-1710" />
                                </button>
                                <button onClick={() => openActionConfirm(insurance, 'deactivate')} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-1711">
                                  <PowerOffIcon className="w-4 h-4" data-id="element-1712" />
                                </button>
                              </>}
                          </> : canDelete('insurance') && <button onClick={() => restoreItem(insurance.id)} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-1713">
                              <RotateCcwIcon className="w-4 h-4" data-id="element-1714" />
                            </button>}
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="px-6 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4" data-id="element-1715">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-1716" />
        </div>
      </Card>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedInsurance ? 'Editar Seguro' : 'Nuevo Seguro'} size="md" icon={<ShieldIcon className="w-5 h-5" data-id="element-1718" />} accentColor="sky" data-id="element-1717">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1719">
          {/* Insurance Info */}
          <div data-id="element-1720">
            <div className="flex items-center gap-2 mb-4" data-id="element-1721">
              <span className="w-1.5 h-5 rounded-full bg-sky-500" data-id="element-1722"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-1723">
                Información del Seguro
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-1724">
              <Input label="Nombre del Seguro" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="Ej: ARS Humano" data-id="element-1725" />
              <div className="grid grid-cols-2 gap-4" data-id="element-1726">
                <Input label="Compañía" value={formData.company} onChange={e => setFormData({
                ...formData,
                company: e.target.value
              })} error={formErrors.company} placeholder="Ej: Humano Seguros" data-id="element-1727" />
                <Input label="Tipo de Cobertura" value={formData.coverageType} onChange={e => setFormData({
                ...formData,
                coverageType: e.target.value
              })} error={formErrors.coverageType} placeholder="Ej: Completo, Básico" data-id="element-1728" />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div data-id="element-1729">
            <div className="flex items-center gap-2 mb-4" data-id="element-1730">
              <span className="w-1.5 h-5 rounded-full bg-cyan-500" data-id="element-1731"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-1732">
                Contacto
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-1733">
              <div className="grid grid-cols-2 gap-4" data-id="element-1734">
                <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} placeholder="809-555-0000" data-id="element-1735" />
                <Input type="email" label="Correo Electrónico" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} placeholder="info@seguro.com" data-id="element-1736" />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-1737">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-1738">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-1739">
              {selectedInsurance ? 'Guardar Cambios' : 'Crear Seguro'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Action Confirmation Modal */}
      <Modal isOpen={isActionConfirmOpen} onClose={() => setIsActionConfirmOpen(false)} title={actionType === 'archive' ? 'Archivar Seguro' : actionType === 'deactivate' ? 'Desactivar Seguro' : 'Eliminar Seguro'} size="sm" data-id="element-1740">
        <div className="space-y-4" data-id="element-1741">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-1742">
            {actionType === 'archive' && `¿Estás seguro de que deseas archivar a "${selectedInsurance?.name}"? Podrás restaurarlo más tarde.`}
            {actionType === 'deactivate' && `¿Estás seguro de que deseas desactivar a "${selectedInsurance?.name}"? No aparecerá en las listas activas.`}
            {actionType === 'delete' && `¿Estás seguro de que deseas eliminar permanentemente a "${selectedInsurance?.name}"? Esta acción no se puede deshacer.`}
          </p>
          <div className="flex justify-end gap-3" data-id="element-1743">
            <Button variant="outline" onClick={() => setIsActionConfirmOpen(false)} data-id="element-1744">
              Cancelar
            </Button>
            <Button variant={actionType === 'delete' ? 'danger' : 'primary'} onClick={handleAction} data-id="element-1745">
              {actionType === 'archive' ? 'Archivar' : actionType === 'deactivate' ? 'Desactivar' : 'Eliminar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}